import React, { useState, useEffect, useCallback } from 'react';
import CastingIndexComponent from './Index.component';
import { getCastingLogList, ICastingLog } from 'api/student/v2/castingLog.api';

interface GroupedCastingLog {
    month: string;
    agencies: {
        agencyId: number;
        agencyName: string;
        logoImage: any;
        castingCount: number;
    }[];
}

const CastingIndexContainer: React.FC = () => {
    const [castingLogs, setCastingLogs] = useState<ICastingLog[]>([]);
    const [groupedData, setGroupedData] = useState<GroupedCastingLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // 캐스팅 로그를 월별/기획사별로 그룹화하는 함수
    const groupCastingLogs = useCallback((logs: ICastingLog[]): GroupedCastingLog[] => {
        // castingDate 역순 정렬 (최신 먼저)
        const sortedLogs = [...logs].sort((a, b) => {
            return b.castingDate.localeCompare(a.castingDate);
        });

        // 월별로 그룹화
        const monthMap = new Map<string, Map<number, {
            agencyId: number;
            agencyName: string;
            logoImage: any;
            castingCount: number;
        }>>();

        sortedLogs.forEach(log => {
            const month = log.castingDate; // "2025-10" 형식
            
            if (!monthMap.has(month)) {
                monthMap.set(month, new Map());
            }

            const agencyMap = monthMap.get(month)!;
            const agencyId = log.appAgency.id;

            if (agencyMap.has(agencyId)) {
                // 이미 있는 기획사면 카운트 증가
                const existing = agencyMap.get(agencyId)!;
                existing.castingCount += 1;
            } else {
                // 새로운 기획사면 추가
                agencyMap.set(agencyId, {
                    agencyId: log.appAgency.id,
                    agencyName: log.appAgency.name || 'Unknown Agency',
                    logoImage: log.appAgency.logoImage,
                    castingCount: 1
                });
            }
        });

        // Map을 배열로 변환
        const grouped: GroupedCastingLog[] = [];
        monthMap.forEach((agencyMap, month) => {
            const agencies = Array.from(agencyMap.values()).sort((a, b) => {
                // 캐스팅 횟수 내림차순, 같으면 이름 순
                if (b.castingCount !== a.castingCount) {
                    return b.castingCount - a.castingCount;
                }
                return a.agencyName.localeCompare(b.agencyName);
            });

            grouped.push({
                month,
                agencies
            });
        });

        return grouped;
    }, []);

    // 첫 페이지 데이터 로드
    const fetchInitialData = useCallback(async () => {
        try {
            setIsLoading(true);
            const result = await getCastingLogList(1);
            
            if (result?.castingLogList) {
                setCastingLogs(result.castingLogList);
                setGroupedData(groupCastingLogs(result.castingLogList));
                setHasMore(result.pagination?.hasNextPage || false);
                setCurrentPage(1);
            } else {
                setCastingLogs([]);
                setGroupedData([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('캐스팅 로그 조회 실패:', error);
            setCastingLogs([]);
            setGroupedData([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, [groupCastingLogs]);

    // 추가 데이터 로드
    const handleLoadMore = useCallback(async () => {
        if (isLoadingMore || !hasMore) return;

        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            const result = await getCastingLogList(nextPage);

            if (result?.castingLogList && result.castingLogList.length > 0) {
                const newLogs = [...castingLogs, ...result.castingLogList];
                setCastingLogs(newLogs);
                setGroupedData(groupCastingLogs(newLogs));
                setHasMore(result.pagination?.hasNextPage || false);
                setCurrentPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('추가 데이터 로드 실패:', error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [castingLogs, currentPage, hasMore, isLoadingMore, groupCastingLogs]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    return (
        <CastingIndexComponent
            groupedData={groupedData}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
        />
    );
};

export default CastingIndexContainer;

